import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Table, Card, Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { FaDownload, FaPrint, FaFilePdf } from 'react-icons/fa';
import C from '../lib/Config';
import swal from 'sweetalert';
import Side from './Sidebar';
import Top from './Topbar';

const ViewInvoice = () => {
  const { invoiceNumber } = useParams();
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instituteDetails, setInstituteDetails] = useState(null);

  // Table styles
  const tableStyle = {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '100%'
  };

  const thStyle = {
    backgroundColor: '#f8f9fa',
    borderTop: '2px solid #999',
    borderBottom: '2px solid #ddd',
    padding: '8px',
    fontWeight: '600'
  };

  const tdStyle = {
    border: '1px solid #ddd',
    padding: '8px'
  };

  const summaryThStyle = {
    ...thStyle,
    width: '40%'
  };

  const summaryTdStyle = {
    ...tdStyle,
    textAlign: 'right'
  };

  const infoBoxStyle = {
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '15px',
    backgroundColor: '#f9f9f9',
    height: '100%'
  };

  const boxTitleStyle = {
    marginBottom: '15px',
    paddingBottom: '8px',
    borderBottom: '1px solid #eee',
    color: '#2c3e50'
  };


  
  useEffect(() => {
    const fetchInstituteDetails = async (instituteInfo) => {
      try {
        const instituteId = instituteInfo.instituteId;
        
        if (!instituteId) {
          console.error('No institute ID found');
          return {
            instituteName: instituteInfo.instituteName || 'Healthcare Facility',
            instituteId: null,
            address: '',
            city: '',
            state: '',
            pinCode: '',
            contactNumber: '',
            email: ''
          };
        }
    
        const instituteResponse = await fetch(`${C.getUrl()}/institute/getInstituteDetailsById/${instituteId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
    
        if (!instituteResponse.ok) {
          throw new Error(`HTTP error! status: ${instituteResponse.status}`);
        }
    
        const responseData = await instituteResponse.json();
        
        // Check if response has expected structure
        if (responseData.statusCode !== 200 || !responseData.message) {
          throw new Error('Invalid response structure');
        }
    
        // Parse the nested message string
        let instituteDetails;
        try {
          instituteDetails = JSON.parse(responseData.message);
        } catch (parseError) {
          console.error('Failed to parse institute details:', parseError);
          throw new Error('Invalid JSON in message');
        }
    
        // Construct full address using all available address components
        const addressParts = [
          instituteDetails.addrLine1,
          instituteDetails.addrLine2,
          instituteDetails.cityMstDTO?.cityName,
          instituteDetails.stateMstDTO?.stateName,
          instituteDetails.zipPinCode,
          
        ].filter(Boolean); // This removes any undefined, null or empty string values
    
        const fullAddress = addressParts.join(', ');
    
        return {
          instituteName: instituteDetails.instituteName || instituteInfo.instituteName || 'Healthcare Facility',
          instituteId: instituteId,
          address: fullAddress || '',
          city: instituteDetails.cityMstDTO?.cityName || '',
          state: instituteDetails.stateMstDTO?.stateName || '',
          pinCode: instituteDetails.pinCode || '',
          contactNumber: instituteDetails.contactNumber || '',
          email: instituteDetails.emailId || '',
        };
      } catch (error) {
        console.error('Error fetching institute details:', error);
        
        // Fallback to basic institute info
        return {
          instituteName: instituteInfo.instituteName || 'Healthcare Facility',
          instituteId: instituteInfo.instituteId,
          address: '',
          city: '',
          state: '',
          pinCode: '',
          contactNumber: '',
          email: ''
        };
      }
    };

    const fetchInvoiceDetails = async () => {
      try {
        // Fetch invoice details
        const invoiceResponse = await fetch(`${C.getUrl()}/invoice/getByInvoiceNumber/${invoiceNumber}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
  
        if (!invoiceResponse.ok) {
          throw new Error('Failed to fetch invoice details');
        }
  
        const invoiceData = await invoiceResponse.json();
        
        let parsedInvoiceData = null;
        if (invoiceData.statusCode === 200 && invoiceData.message) {
          parsedInvoiceData = JSON.parse(invoiceData.message);
          setInvoiceDetails(parsedInvoiceData);
        }
  
        // Fetch invoice items
        const itemsResponse = await fetch(`${C.getUrl()}/invoice/item/getByInvoiceNumber/${invoiceNumber}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
  
        if (!itemsResponse.ok) {
          throw new Error('Failed to fetch invoice items');
        }
  
        const itemsData = await itemsResponse.json();
        
        if (itemsData.statusCode === 200 && itemsData.message) {
          const parsedItemsData = JSON.parse(itemsData.message);
          setInvoiceItems(parsedItemsData.itemList || []);
        }
  
        // Fetch invoice payments
        const paymentsResponse = await fetch(`${C.getUrl()}/invoice/payment/getByInvoiceNumber/${invoiceNumber}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
  
        if (!paymentsResponse.ok) {
          throw new Error('Failed to fetch invoice payments');
        }
  
        const paymentsData = await paymentsResponse.json();
        
        if (paymentsData.statusCode === 200 && paymentsData.message) {
          const parsedPaymentsData = JSON.parse(paymentsData.message);
          setInvoicePayments(parsedPaymentsData.paymentList || []);
        }
  
        // Now fetch patient details to get institute ID
        if (parsedInvoiceData && parsedInvoiceData.patientId) {
          try {
            const patientResponse = await fetch(`${C.getUrl()}/userCase/getPatientDetail/${parsedInvoiceData.patientId}`, {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' }
            });
  
            if (patientResponse.ok) {
              const patientData = await patientResponse.json();
              
              if (patientData.statusCode === 200 && patientData.message) {
                const parsedPatientData = JSON.parse(patientData.message);
                
                // Check if institute information exists
                if (parsedPatientData.instituteDto) {
                  const instituteInfo = parsedPatientData.instituteDto;
                  
                  // Use the new fetchInstituteDetails function
                  const detailedInstituteInfo = await fetchInstituteDetails(instituteInfo);
                  
                  setInstituteDetails(detailedInstituteInfo);
                }
              }
            }
          } catch (error) {
            console.error('Error fetching patient details:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching invoice details:', error);
        swal({
          title: "Error!",
          text: "Failed to fetch invoice details",
          icon: "error"
        });
      } finally {
        setLoading(false);
      }
    };
  
    fetchInvoiceDetails();
  }, [invoiceNumber]);
  

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Calculate total amount (sum of all items)
  const calculateTotalAmount = () => {
    return invoiceItems.reduce((total, item) => 
      total + ((item.costPerUnit || 0) * (item.quantity || 1)), 0);
  };

  // Calculate net amount (should be the same as total amount in this case)
  const calculateNetAmount = () => {
    return calculateTotalAmount();
  };

  // Calculate subtotal (net amount - (discount + tax))
  const calculateSubtotal = (netAmount, discount, tax) => {
    return netAmount - discount - tax;
  };

  // Handle standard print
  const handlePrint = () => {
    generatePrintWindow(false);
  };

  // Handle detailed print
  const handleDetailedPrint = () => {
    generatePrintWindow(true);
  };

  // Generate print window
  const generatePrintWindow = (detailed) => {
    try {
      // Calculate important values
      const netAmount = calculateNetAmount();
      const received = invoiceDetails.received || 0;
      const discount = invoiceDetails.discount || 0;
      const tax = 0; // No tax in the current implementation
      const subtotal = calculateSubtotal(netAmount, discount, tax);
      const due = netAmount - received - discount;
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      
      // Get institute information
      const instituteName = instituteDetails?.instituteName || 'Healthcare Facility';
      
      // Generate HTML content for printing
      let printContent = `
        <html>
          <head>
            <title>Invoice - ${invoiceDetails.invoiceNumber}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
              .section { margin-bottom: 15px; }
              .section-title { font-weight: bold; color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 5px; }
              table { width: 100%; margin-top: 10px; border-collapse: separate; border-spacing: 0; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; position: relative; }
              th { background-color: #f2f2f2; border-top: 2px solid #999; border-bottom: 2px solid #ddd; }
              thead tr { border-top: 2px solid #999; }
              td { border: 1px solid #ddd; }
              .totals { display: flex; justify-content: flex-end; margin-top: 20px; }
              .totals-table { width: 300px; }
              .amount-in-words { margin-top: 20px; font-style: italic; }
              .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 20px; }
              .info-box { border: 1px solid #ddd; padding: 15px; border-radius: 5px; background-color: #f9f9f9; }
              .info-box h3 { margin-top: 0; color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 8px; }
              .info-box p { margin-bottom: 5px; }
              .institute-header { text-align: center; margin-bottom: 15px; }
              .institute-header h2 { margin-bottom: 5px; color: #2c3e50; }
              .institute-header p { margin: 0; color: #555; }
            </style>
          </head>
          <body>
            <div class="institute-header">
              <h2>${instituteName}</h2>
              ${instituteDetails?.address ? `<p>${instituteDetails.address}</p>` : ''}
            </div>
            
            <div style="text-align: center; margin-bottom: 20px;">
              <h1 style="color: #333;">INVOICE</h1>
            </div>
            
            <div class="info-grid">
                <div class="info-box">
                    <h3>Invoice Details</h3>
                    <p><strong>Invoice Number:</strong> ${invoiceDetails.invoiceNumber}</p>
                    <p><strong>Invoice Date:</strong> ${formatDate(invoiceDetails.invoiceDate)}</p>
                    ${invoiceDetails.department === 'IPD' ? 
                      `<p><strong>Admission Date:</strong> ${formatDate(invoiceDetails.admission_Date) || 'N/A'}</p>
                      <p><strong>Discharge Date:</strong> ${formatDate(invoiceDetails.discharge_Date) || 'N/A'}</p>` : 
                      `<p><strong>Visit Date:</strong> ${formatDate(invoiceDetails.visitDate) || 'N/A'}</p>`
                    }
                    <p><strong>Department:</strong> ${invoiceDetails.department || 'N/A'}</p>
                </div>
              <div class="info-box">
                <h3>Patient Information</h3>
                <p><strong>Name:</strong> ${invoiceDetails.patientName}</p>
                <p><strong>Patient ID:</strong> ${invoiceDetails.patientId || 'N/A'}</p>
                <p><strong>Phone Number:</strong> ${invoiceDetails.phoneNumber1 || invoiceDetails.mobileNumber || 'N/A'}</p>
                <p><strong>Primary Consultant:</strong> ${invoiceDetails.primaryConsultant || invoiceDetails.doctor || 'N/A'}</p>
              </div>
            </div>
            
            <div class="section">
              <h3 class="section-title">Invoice Items</h3>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Category</th>
                    <th>Cost Per Unit</th>
                    <th>Quantity</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${invoiceItems.map(item => `
                    <tr>
                      <td>${item.item || 'N/A'}</td>
                      <td>${item.category || 'N/A'}</td>
                      <td>₹${(item.costPerUnit || 0).toFixed(2)}</td>
                      <td>${item.quantity || 1}</td>
                      <td>₹${((item.costPerUnit || 0) * (item.quantity || 1)).toFixed(2)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
      `;
  
      if (detailed && invoicePayments.length > 0) {
        printContent += `
          <div class="section">
            <h3 class="section-title">Payment Information</h3>
            <table>
              <thead>
                <tr>
                  <th>Payment Method</th>
                  <th>Amount</th>
                  <th>Other Note</th>
                  <th>Payment Date</th>
                </tr>
              </thead>
              <tbody>
                ${invoicePayments.map(payment => `
                  <tr>
                    <td>${payment.paymentMethod}</td>
                    <td>₹${(payment.paymentAmount || 0).toFixed(2)}</td>
                    <td>${payment.otherNote || ''}</td>
                    <td>${formatDate(payment.paymentDate)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      }
  
      printContent += `
            <div class="totals">
              <table class="totals-table">
                <tr>
                  <th>Net Amount</th>
                  <td>₹${netAmount.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Discount</th>
                  <td>₹${discount.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Tax</th>
                  <td>₹${tax.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Subtotal</th>
                  <td>₹${subtotal.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Amount Received</th>
                  <td>₹${received.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Due Amount</th>
                  <td>₹${due.toFixed(2)}</td>
                </tr>
              </table>
            </div>
            
            <div class="amount-in-words">
              <p><strong>Amount in words:</strong> ${convertAmountToWords(netAmount)}</p>
            </div>
          </body>
        </html>
      `;
      
      // Write the content to the print window
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = function() {
        printWindow.print();
      };
    } catch (error) {
      console.error('Error generating print:', error);
      swal({
        title: "Error!",
        text: "Failed to generate print view",
        icon: "error"
      });
    }
  };

  const handleDownloadPdf = () => {
    try {
      // Create a special print window designed for PDF saving
      const printWindow = window.open('', '_blank');
      
      // Calculate important values
      const netAmount = calculateNetAmount();
      const received = invoiceDetails.received || 0;
      const discount = invoiceDetails.discount || 0;
      const tax = 0; // No tax in the current implementation
      const subtotal = calculateSubtotal(netAmount, discount, tax);
      const due = netAmount - received - discount;
      
      // Get institute information
      const instituteName = instituteDetails?.instituteName || 'Healthcare Facility';
      const instituteAddress = instituteDetails?.address || 'Address not available';
      const instituteContact = instituteDetails?.contactNumber || '';
      const instituteEmail = instituteDetails?.email || '';
      
      // Generate HTML content for PDF
      const printContent = `
        <html>
          <head>
            <title>Invoice-${invoiceDetails.invoiceNumber}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
              .section { margin-bottom: 15px; }
              .section-title { font-weight: bold; color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 5px; }
              table { width: 100%; margin-top: 10px; border-collapse: separate; border-spacing: 0; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; position: relative; }
              th { background-color: #f2f2f2; border-top: 2px solid #999; border-bottom: 2px solid #ddd; }
              thead tr { border-top: 2px solid #999; }
              td { border: 1px solid #ddd; }
              .totals { display: flex; justify-content: flex-end; margin-top: 20px; }
              .totals-table { width: 300px; }
              .amount-in-words { margin-top: 20px; font-style: italic; }
              .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 20px; }
              .info-box { border: 1px solid #ddd; padding: 15px; border-radius: 5px; background-color: #f9f9f9; }
              .info-box h3 { margin-top: 0; color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 8px; }
              .info-box p { margin-bottom: 5px; }
              .institute-header { text-align: center; margin-bottom: 15px; }
              .institute-header h2 { margin-bottom: 5px; color: #2c3e50; }
              .institute-header p { margin: 0; color: #555; }
              @media print {
                body { margin: 0; padding: 0; }
                .info-box { background-color: #f9f9f9 !important; -webkit-print-color-adjust: exact; }
                th { background-color: #f2f2f2 !important; -webkit-print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            <div class="institute-header">
              <h2>${instituteName}</h2>
              <p>${instituteAddress}</p>
              ${instituteContact ? `<p>Contact: ${instituteContact}</p>` : ''}
              ${instituteEmail ? `<p>Email: ${instituteEmail}</p>` : ''}
            </div>
            
            <!-- PDF header -->
            <div style="text-align: center; margin-bottom: 20px;">
              <h1 style="color: #333;">INVOICE</h1>
            </div>
            
            <div class="info-grid">
               <div class="info-box">
                  <h3>Invoice Details</h3>
                  <p><strong>Invoice Number:</strong> ${invoiceDetails.invoiceNumber}</p>
                  <p><strong>Invoice Date:</strong> ${formatDate(invoiceDetails.invoiceDate)}</p>
                  ${invoiceDetails.department === 'IPD' ? 
                    `<p><strong>Admission Date:</strong> ${formatDate(invoiceDetails.admission_Date) || 'N/A'}</p>
                    <p><strong>Discharge Date:</strong> ${formatDate(invoiceDetails.discharge_Date) || 'N/A'}</p>` : 
                    `<p><strong>Visit Date:</strong> ${formatDate(invoiceDetails.visitDate) || 'N/A'}</p>`
                  }
                  <p><strong>Department:</strong> ${invoiceDetails.department || 'N/A'}</p>
                </div>
              <div class="info-box">
                <h3>Patient Information</h3>
                <p><strong>Name:</strong> ${invoiceDetails.patientName}</p>
                <p><strong>Patient ID:</strong> ${invoiceDetails.patientId || 'N/A'}</p>
                <p><strong>Phone Number:</strong> ${invoiceDetails.phoneNumber1 || invoiceDetails.mobileNumber || 'N/A'}</p>
                <p><strong>Primary Consultant:</strong> ${invoiceDetails.primaryConsultant || invoiceDetails.doctor || 'N/A'}</p>
              </div>
            </div>
            
            <div class="section">
              <h3 class="section-title">Invoice Items</h3>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Category</th>
                    <th>Cost Per Unit</th>
                    <th>Quantity</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${invoiceItems.map(item => `
                    <tr>
                      <td>${item.item || 'N/A'}</td>
                      <td>${item.category || 'N/A'}</td>
                      <td>₹${(item.costPerUnit || 0).toFixed(2)}</td>
                      <td>${item.quantity || 1}</td>
                      <td>₹${((item.costPerUnit || 0) * (item.quantity || 1)).toFixed(2)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            
            ${invoicePayments.length > 0 ? `
              <div class="section">
                <h3 class="section-title">Payment Information</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Payment Method</th>
                      <th>Amount</th>
                      <th>Other Note</th>
                      <th>Payment Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${invoicePayments.map(payment => `
                      <tr>
                        <td>${payment.paymentMethod}</td>
                        <td>₹${(payment.paymentAmount || 0).toFixed(2)}</td>
                        <td>${payment.otherNote || ''}</td>
                        <td>${formatDate(payment.paymentDate)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            ` : ''}
            
            <div class="totals">
              <table class="totals-table">
                <tr>
                  <th>Net Amount</th>
                  <td>₹${netAmount.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Discount</th>
                  <td>₹${discount.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Tax</th>
                  <td>₹${tax.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Subtotal</th>
                  <td>₹${subtotal.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Amount Received</th>
                  <td>₹${received.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Due Amount</th>
                  <td>₹${due.toFixed(2)}</td>
                </tr>
              </table>
            </div>
            
            <div class="amount-in-words">
              <p><strong>Amount in words:</strong> ${convertAmountToWords(netAmount)}</p>
            </div>
            
            <script>
              // Auto-prompt save dialog
              window.onload = function() {
                document.title = "Invoice-${invoiceDetails.invoiceNumber}";
                window.print();
              };
            </script>
          </body>
        </html>
      `;
      
      // Write the content to the print window
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      swal({
        title: "Error!",
        text: "Failed to generate PDF view",
        icon: "error"
      });
    }
  };
  
  

  // Convert amount to words
  const convertAmountToWords = (amount) => {
    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      
      let words = '';
      if (n >= 100) {
        words += units[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      
      if (n >= 20) {
        words += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      }
      
      if (n >= 10) {
        words += teens[n - 10] + ' ';
      } else if (n > 0) {
        words += units[n] + ' ';
      }
      
      return words;
    };

    const amountInt = Math.floor(amount);
    const amountPaise = Math.round((amount - amountInt) * 100);

    let words = '';
    const crore = Math.floor(amountInt / 10000000);
    const lakhs = Math.floor((amountInt % 10000000) / 100000);
    const thousands = Math.floor((amountInt % 100000) / 1000);
    const hundreds = amountInt % 1000;

    if (crore > 0) words += convertLessThanThousand(crore) + 'Crore ';
    if (lakhs > 0) words += convertLessThanThousand(lakhs) + 'Lakh ';
    if (thousands > 0) words += convertLessThanThousand(thousands) + 'Thousand ';
    if (hundreds > 0) words += convertLessThanThousand(hundreds);

    words = words.trim() + ' Rupees';
    
    if (amountPaise > 0) {
      words += ' and ' + convertLessThanThousand(amountPaise) + 'Paise';
    }

    return words + ' Only';
  };

  if (loading) {
    return (
      <div className="d-flex" id="wrapper">
        <Side />
        <div id="content-wrapper" className="d-flex flex-column">
          <div id="content">
            <Top />
            <Container fluid>
              <div className="text-center mt-5">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </Container>
          </div>
        </div>
      </div>
    );
  }

  if (!invoiceDetails) {
    return (
      <div className="d-flex" id="wrapper">
        <Side />
        <div id="content-wrapper" className="d-flex flex-column">
          <div id="content">
            <Top />
            <Container fluid>
              <div className="text-center mt-5">
                <p>No invoice details found</p>
              </div>
            </Container>
          </div>
        </div>
      </div>
    );
  }

  // Calculate all the financial values
  const netAmount = calculateNetAmount();
  const received = invoiceDetails.received || 0;
  const discount = invoiceDetails.discount || 0;
  const tax = 0; // No tax in the current implementation
  const subtotal = calculateSubtotal(netAmount, discount, tax);
  const due = netAmount - received - discount;

  return (
    <div className="d-flex" id="wrapper">
      <Side />
      <div id="content-wrapper" className="d-flex flex-column">
        <div id="content">
          <Top />
          {/* Action Bar */}
          <div style={{ backgroundColor: '#7FD1BA6B', borderBottom: '1px solid #dee2e6', padding: '0.5rem 1.5rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="download-tooltip">Download PDF</Tooltip>}
            >
              <Button 
                variant="light" 
                className="mr-2" 
                onClick={handleDownloadPdf}
              >
                <FaDownload />
              </Button>
            </OverlayTrigger>
            
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="print-tooltip">Print</Tooltip>}
            >
              <Button 
                variant="light" 
                className="mr-2" 
                onClick={handlePrint}
              >
                <FaPrint />
              </Button>
            </OverlayTrigger>
            
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="detailed-print-tooltip">Detailed Print</Tooltip>}
            >
              <Button 
                variant="light" 
                onClick={handleDetailedPrint}
              >
                <FaFilePdf /> Detailed Print
              </Button>
            </OverlayTrigger>
          </div>
          
          <Container fluid style={{ padding: '1.5rem' }}>
            {/* Institute Header */}
            {instituteDetails && (
              <Row className="mb-4">
                <Col>
                  <Card className="text-center">
                    <Card.Body>
                      <h4>{instituteDetails.instituteName || 'Healthcare Facility'}</h4>
                      <p className="mb-1">{instituteDetails.address || 'Address not available'}</p>
                      {instituteDetails.contactNumber && <p className="mb-1">Contact: {instituteDetails.contactNumber}</p>}
                      {instituteDetails.email && <p className="mb-0">Email: {instituteDetails.email}</p>}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
            
            <Row>
              <Col>
                <Card>
                  <Card.Body>
                    <Row style={{ marginBottom: '1.5rem' }}>
                      <Col md={6}>
                      <div style={infoBoxStyle}>
                          <h5 style={boxTitleStyle}>Invoice Details</h5>
                          <p style={{ marginBottom: '0.5rem' }}><strong>Invoice Number:</strong> {invoiceDetails.invoiceNumber}</p>
                          <p style={{ marginBottom: '0.5rem' }}><strong>Invoice Date:</strong> {formatDate(invoiceDetails.invoiceDate)}</p>
                          
                          {/* Conditionally show dates based on department */}
                          {invoiceDetails.department === 'IPD' ? (
                            <>
                              <p style={{ marginBottom: '0.5rem' }}><strong>Admission Date:</strong> {formatDate(invoiceDetails.admission_Date) || 'N/A'}</p>
                              <p style={{ marginBottom: '0.5rem' }}><strong>Discharge Date:</strong> {formatDate(invoiceDetails.discharge_Date) || 'N/A'}</p>
                            </>
                          ) : (
                            <p style={{ marginBottom: '0.5rem' }}><strong>Visit Date:</strong> {formatDate(invoiceDetails.visitDate) || 'N/A'}</p>
                          )}
                          
                          <p style={{ marginBottom: '0.5rem' }}><strong>Department:</strong> {invoiceDetails.department || 'N/A'}</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div style={infoBoxStyle}>
                          <h5 style={boxTitleStyle}>Patient Information</h5>
                          <p style={{ marginBottom: '0.5rem' }}><strong>Patient Name:</strong> {invoiceDetails.patientName}</p>
                          <p style={{ marginBottom: '0.5rem' }}><strong>Patient ID:</strong> {invoiceDetails.patientId || 'N/A'}</p>
                          <p style={{ marginBottom: '0.5rem' }}><strong>Phone Number:</strong> {invoiceDetails.phoneNumber1 || invoiceDetails.mobileNumber || 'N/A'}</p>
                          <p style={{ marginBottom: '0.5rem' }}><strong>Primary Consultant:</strong> {invoiceDetails.primaryConsultant || invoiceDetails.doctor || 'N/A'}</p>
                        </div>
                      </Col>
                    </Row>
  
                    <h5 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Invoice Items</h5>
                    <div style={{ overflowX: 'auto' }}>
                      <Table bordered hover responsive>
                        <thead>
                          <tr>
                            {['Description', 'Category', 'Cost Per Unit', 'Quantity', 'Total Amount'].map(header => (
                              <th key={header} style={thStyle}>{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceItems.map((item, index) => (
                            <tr key={index}>
                              <td style={tdStyle}>{item.item || 'N/A'}</td>
                              <td style={tdStyle}>{item.category || 'N/A'}</td>
                              <td style={tdStyle}>₹{(item.costPerUnit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td style={tdStyle}>{item.quantity || 1}</td>
                              <td style={tdStyle}>₹{((item.costPerUnit || 0) * (item.quantity || 1)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
  
                    <Row style={{ marginTop: '1.5rem' }}>
                      <Col md={6}>
                        <div style={{ ...infoBoxStyle, minHeight: '150px' }}>
                          <p style={{ marginBottom: '0.5rem' }}><strong>Amount in Words:</strong></p>
                          <p>{convertAmountToWords(netAmount)}</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <Table bordered style={tableStyle}>
                          <tbody>
                            <tr>
                              <th style={summaryThStyle}>Net Amount</th>
                              <td style={summaryTdStyle}>₹{netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                            <tr>
                              <th style={summaryThStyle}>Discount</th>
                              <td style={summaryTdStyle}>₹{discount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                            <tr>
                              <th style={summaryThStyle}>Tax</th>
                              <td style={summaryTdStyle}>₹{tax.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                            <tr>
                              <th style={summaryThStyle}>Subtotal</th>
                              <td style={summaryTdStyle}>₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                            <tr>
                              <th style={summaryThStyle}>Amount Received</th>
                              <td style={summaryTdStyle}>₹{received.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                            <tr>
                              <th style={summaryThStyle}>Due Amount</th>
                              <td style={summaryTdStyle}>₹{due.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </Col>
                    </Row>
  
                    {invoicePayments.length > 0 && (
                      <div style={{ marginTop: '1.5rem' }}>
                        <h5 style={{ marginBottom: '1rem' }}>Payment Status: {invoiceDetails.status || 'Completed'}</h5>
                        <div style={{ overflowX: 'auto' }}>
                          <Table bordered hover responsive>
                            <thead>
                              <tr>
                                {['Payment Method', 'Amount', 'Other Note', 'Payment Date'].map(header => (
                                  <th key={header} style={thStyle}>{header}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {invoicePayments.map((payment, index) => (
                                <tr key={index}>
                                  <td style={tdStyle}>{payment.paymentMethod}</td>
                                  <td style={tdStyle}>₹{(payment.paymentAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td style={tdStyle}>{payment.otherNote || ''}</td>
                                  <td style={tdStyle}>{formatDate(payment.paymentDate)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </div>
  );
};

export default ViewInvoice;